import { Button, InputNumber, Select } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { flyToGeometry } from "../../../../ExploreArchive/ultis";
import { useMapContext } from "../../../core/MapContext";
import { addAOIFeature, updateAOIFeature } from "../../../store/slices/aoiSlice";
import type { DrawAOIFeature } from "../types";
import { AOI_FEATURE_KEY } from "../consts";

type CoordFormat = "lat, lng degrees" | "lat, lng minutes";

interface DMSParts
{
  deg: number | null;
  min: number | null;
  sec: number | null;
}

const EMPTY_DMS: DMSParts = { deg: null, min: null, sec: null };

// Combine the 3 DMS boxes into a decimal degree value. The sign is taken from
// the degrees box (negative degrees = South / West); minutes and seconds are
// always treated as magnitudes.
const dmsToDecimal = ({ deg, min, sec }: DMSParts): number | null =>
{
  if (deg === null && min === null && sec === null) return null;
  const d = deg ?? 0;
  const m = min ?? 0;
  const s = sec ?? 0;
  const sign = d < 0 ? -1 : 1;
  return sign * (Math.abs(d) + m / 60 + s / 3600);
};

// Split a decimal degree value into the 3 DMS boxes so values carry over when
// the user switches format or edits an existing point.
const decimalToDMSParts = (decimal: number | null): DMSParts =>
{
  if (decimal === null || decimal === undefined || Number.isNaN(decimal)) return EMPTY_DMS;
  const sign = decimal < 0 ? -1 : 1;
  const abs = Math.abs(decimal);
  const deg = Math.floor(abs);
  const minDecimal = (abs - deg) * 60;
  const min = Math.floor(minDecimal);
  const sec = Math.round((minDecimal - min) * 60);
  return { deg: sign * deg, min, sec };
};

interface FormInputPointAOIProps
{
  toggleInputPoint: () => void;
  editingFeature?: DrawAOIFeature | null;
}

export default function FormInputPointAOI({ toggleInputPoint, editingFeature }: FormInputPointAOIProps)
{
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { mapInstance } = useMapContext();

  const editingCoords = editingFeature?.geometry.type === "Point" ? editingFeature.geometry.coordinates : null;

  const [format, setFormat] = useState<CoordFormat>("lat, lng degrees");

  // Decimal values — source of truth for the "degrees" format.
  const [lat, setLat] = useState<number | null>(editingCoords ? editingCoords[1] : null);
  const [lng, setLng] = useState<number | null>(editingCoords ? editingCoords[0] : null);

  // DMS values — source of truth for the "minutes" format.
  const [latDMS, setLatDMS] = useState<DMSParts>(
    editingCoords ? decimalToDMSParts(editingCoords[1]) : EMPTY_DMS,
  );
  const [lngDMS, setLngDMS] = useState<DMSParts>(
    editingCoords ? decimalToDMSParts(editingCoords[0]) : EMPTY_DMS,
  );

  const isMinutes = format === "lat, lng minutes";

  // Resolve the effective decimal coordinates from whichever format is active.
  const latValue = isMinutes ? dmsToDecimal(latDMS) : lat;
  const lngValue = isMinutes ? dmsToDecimal(lngDMS) : lng;

  const isValid =
    latValue !== null &&
    lngValue !== null &&
    latValue >= -90 &&
    latValue <= 90 &&
    lngValue >= -180 &&
    lngValue <= 180;

  // When toggling format, carry the current values across so nothing is lost.
  const handleFormatChange = (value: CoordFormat) =>
  {
    if (value === "lat, lng minutes")
    {
      setLatDMS(decimalToDMSParts(lat));
      setLngDMS(decimalToDMSParts(lng));
    } else
    {
      setLat(dmsToDecimal(latDMS));
      setLng(dmsToDecimal(lngDMS));
    }
    setFormat(value);
  };

  const onSubmit = () =>
  {
    if (!isValid) return;

    const geometry = {
      type: "Point" as const,
      coordinates: [lngValue as number, latValue as number] as [number, number],
    };

    if (editingFeature)
    {
      dispatch(updateAOIFeature({ id: editingFeature.id, geometry }));
    } else
    {
      dispatch(
        addAOIFeature({
          id: `${AOI_FEATURE_KEY}-${crypto.randomUUID()}`,
          geometry,
        }),
      );
    }

    flyToGeometry(geometry, mapInstance);
    toggleInputPoint();
  };

  // A row of 3 boxes (degrees / minutes / seconds) for one coordinate.
  const renderDMSRow = (value: DMSParts, onChange: (next: DMSParts) => void) => (
    <div className="flex gap-2 items-center">
      <InputNumber
        className="w-full div-dropdown"
        value={value.deg}
        onChange={(val) => onChange({ ...value, deg: val })}
        placeholder={t("Degrees")}
      />
      <span className="text-gray-300">°</span>
      <InputNumber
        className="w-full div-dropdown"
        min={0}
        max={59}
        value={value.min}
        onChange={(val) => onChange({ ...value, min: val })}
        placeholder={t("Minutes")}
      />
      <span className="text-gray-300">′</span>
      <InputNumber
        className="w-full div-dropdown"
        min={0}
        max={59}
        value={value.sec}
        onChange={(val) => onChange({ ...value, sec: val })}
        placeholder={t("Seconds")}
      />
      <span className="text-gray-300">″</span>
    </div>
  );

  return (
    <div className="py-2">
      <div className="mb-4">
        <p className="text-sm mb-1 text-gray-300">{t("Format")}</p>
        <Select
          className="w-full div-dropdown"
          value={format}
          onChange={handleFormatChange}
          options={[
            { value: "lat, lng degrees", label: "lat, lng degrees" },
            { value: "lat, lng minutes", label: "lat, lng minutes" },
          ]}
        />
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-sm mb-1 text-gray-300">
            {t("Latitude")} {isMinutes ? "(DMS)" : "(DD)"}
          </p>
          {isMinutes ? (
            renderDMSRow(latDMS, setLatDMS)
          ) : (
            <InputNumber
              className="w-full div-dropdown"
              min={-90}
              max={90}
              value={lat}
              onChange={(val) => setLat(val)}
              placeholder={t("Latitude")}
            />
          )}
        </div>
        <div>
          <p className="text-sm mb-1 text-gray-300">
            {t("Longitude")} {isMinutes ? "(DMS)" : "(DD)"}
          </p>
          {isMinutes ? (
            renderDMSRow(lngDMS, setLngDMS)
          ) : (
            <InputNumber
              className="w-full div-dropdown"
              min={-180}
              max={180}
              value={lng}
              onChange={(val) => setLng(val)}
              placeholder={t("Longitude")}
            />
          )}
        </div>
      </div>

      <div className="flex gap-2 justify-end mt-6">
        <Button className="h-8 w-[100px] btn-outline" onClick={() => toggleInputPoint()}>
          {t("Cancel")}
        </Button>
        <Button
          className="h-8 w-[100px] rounded btn-primary"
          onClick={onSubmit}
          disabled={!isValid}
        >
          {t("Yes")}
        </Button>
      </div>
    </div>
  );
}
